import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import applicationService from '../services/applicationService';
import reportService from '../services/reportService';
import ReportView from '../components/report/ReportView';
import Alert from '../components/common/Alert';
import LoadingSpinner from '../components/common/LoadingSpinner';

const messageFrom = (err, fallback) => err.response?.data?.message || err.message || fallback;

const maskName = (first, last) => {
  const initial = (s) => (s ? `${s[0].toUpperCase()}***` : '');
  const masked = [initial(first), initial(last)].filter(Boolean).join(' ');
  return masked || 'Applicant';
};

// Property.monthly_rent and ApplicantProfile.gross/net_monthly_income are exposed as pre-formatted
// "R 15,000.00" display strings (see Property::getMonthlyRentAttribute, ApplicantProfileResource) —
// only Property has a separate raw numeric field (monthly_rent_amount). Parse either shape back to
// a plain number so ratios can be calculated and formatCurrency doesn't render "RNaN".
const parseAmount = (val) => {
  if (val === null || val === undefined || val === '') return null;
  if (typeof val === 'number') return val;
  const num = parseFloat(String(val).replace(/[^0-9.-]/g, ''));
  return Number.isNaN(num) ? null : num;
};

// Maps the raw VerificationTask.result_data captured per check_type (see IdentityVerificationService,
// IncomeVerificationService, CreditCheckService) into the fields ReportView renders. Any check that
// hasn't run yet (no verification task processed) falls back to a clear "not yet verified" state
// rather than crashing on missing nested fields.
const buildReport = (report, application) => {
  const data = report?.report_data || {};
  const sourceDates = report?.source_dates || {};
  const profile = application?.applicant_profile;
  const rent = parseAmount(application?.property?.monthly_rent_amount ?? application?.property?.monthly_rent);

  const identityRaw = data.identity;
  const identity = {
    method: identityRaw ? 'Document review' : 'Not yet verified',
    result: identityRaw ? (identityRaw.match_result ? 'Match' : 'Mismatch') : 'Pending',
    notes: identityRaw?.limitations || (identityRaw ? `Document validity: ${identityRaw.document_validity}` : 'Identity has not been verified yet.'),
    sourceDate: sourceDates.identity || null,
  };

  const incomeRaw = data.income;
  const declaredGross = parseAmount(incomeRaw?.declared_amount ?? profile?.gross_monthly_income);
  const verifiedGross = parseAmount(incomeRaw?.evidence_amount);
  const declaredNet = parseAmount(profile?.net_monthly_income);
  const income = {
    declaredGross,
    verifiedGross,
    grossRatio: rent && verifiedGross ? Number(((rent / verifiedGross) * 100).toFixed(1)) : null,
    netRatio: rent && declaredNet ? Number(((rent / declaredNet) * 100).toFixed(1)) : null,
    calcDate: sourceDates.income || (incomeRaw ? report.generated_at : null),
  };

  const creditRaw = data.credit?.response_summary;
  const credit = {
    summary: creditRaw ? `Score ${creditRaw.score} (${creditRaw.status})` : 'Not yet verified',
    indicators: creditRaw ? `${creditRaw.active_accounts} active account(s), ${creditRaw.default_history ? 'defaults on record' : 'no defaults'}` : '—',
    provider: 'CreditBureauZA',
  };

  const exceptions = (report?.limitations && report.limitations !== 'None')
    ? report.limitations.split('\n').map((description) => ({ description }))
    : [];

  return {
    propertyAddress: application?.property?.address || '—',
    maskedName: maskName(profile?.first_name, profile?.last_name),
    rent,
    version: report?.version_number,
    generatedAt: report?.generated_at,
    validUntil: null,
    identity,
    income,
    credit,
    exceptions,
  };
};

const ReportPage = () => {
  const { id } = useParams();
  const [report, setReport] = useState(null);
  const [error, setError] = useState('');
  const [noReport, setNoReport] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [application, reports] = await Promise.all([
          applicationService.get(id),
          reportService.getReports(id),
        ]);
        if (cancelled) return;
        if (!reports || reports.length === 0) {
          setNoReport(true);
          return;
        }
        setReport(buildReport(reports[0], application));
      } catch (err) {
        if (!cancelled) setError(messageFrom(err, 'This report could not be loaded.'));
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  if (error) return <Alert type="error" message={error} />;
  if (noReport) return <Alert type="info" message="No report has been generated for this application yet." />;
  if (!report) return <LoadingSpinner fullPage />;

  return (
    <div className="report-page" style={{ padding: '20px' }}>
      <ReportView report={report} />
    </div>
  );
};
export default ReportPage;
